/**
 * <ActivityStatusBadge> — où en est une activité (molecule).
 *
 * La `DoneChip` du legacy (`student/activity/read/DoneChip.js`), en jetons : mêmes statuts, mêmes
 * mots, au « Fait 2x » près qui garde son compteur. Une seule différence assumée : la chip du
 * legacy est rendue SANS texte dans l'en-tête de la fiche, donc un parent lisait une pastille
 * verte sans savoir ce qu'elle disait. Ici le mot est toujours écrit — c'est la règle « le sens ne
 * repose pas sur la seule image ».
 *
 * Ne dessine rien : `<Badge>` porte la chip.
 */
import React from "react"

import { type UIActivityStatus } from "../../../ports/primary/ui/types"
import { Badge, type BadgeVariant } from "../../atoms/Badge"
import { type IconName } from "../../atoms/icons/Icon"

/** Libellé, glyphe et teinte de chaque statut — la table du legacy, à un endroit. */
const STATUS: Record<UIActivityStatus, { label: string; variant: BadgeVariant; icon?: IconName }> = {
    todo: { label: "À faire", variant: "neutral" },
    started: { label: "En pause", variant: "success", icon: "pause" },
    ongoing: { label: "En cours", variant: "success", icon: "hourglass-half" },
    pending_review: { label: "En attente", variant: "warning", icon: "hourglass-half" },
    not_reviewed: { label: "Fait", variant: "success", icon: "check" },
    rejected: { label: "Rejeté", variant: "neutral", icon: "times" },
    validated: { label: "Validé", variant: "success", icon: "check-circle" },
}

export type ActivityStatusBadgeProps = {
    status: UIActivityStatus
    /** Nombre de rendus — « Fait 2x ». Ignoré hors du statut « Fait ». */
    count?: number
    testID?: string
}

export function ActivityStatusBadge(props: ActivityStatusBadgeProps) {
    const { status, count, testID } = props

    const { label, variant, icon } = STATUS[status]
    const text = status === "not_reviewed" && count != null && count > 1 ? `${label} ${count}x` : label

    return <Badge text={text} variant={variant} tinted iconName={icon} testID={testID} />
}
